import { useEffect, useRef, useState } from "react";
import { api, errorText } from "../api";
import { copyToClipboard } from "../clipboard";
import { confirmDialog } from "../dialog";
import { timeAgo } from "../format";
import { useIsFocused, useTick } from "../hooks";
import { DEFAULT_SETTINGS } from "../prefs";
import { toast } from "../toast";
import type {
  Model,
  Preset,
  StagePresence,
  StageStatus,
  Voice,
  VoiceSettings,
} from "../types";
import { isV3, snapStability, ModelPicker } from "./ModelPicker";
import { TuberControls } from "./TuberControls";
import { VoiceSettingsForm } from "./VoiceSettingsForm";

/* The instance's characters.

   A preset is no longer a shortcut in one person's browser — it is a character
   the whole instance speaks as, and optionally a browser source of its own. That
   makes this the screen where a streamer sets up a second (and third) character:
   voice on the left, where it stands on the right, and the OBS URL underneath.

   Two kinds of edit, deliberately handled differently:

     the identity  name, voice, model, tuning — a form with a save button,
                   because half-typed changes must not reach a live stream;
     the staging   own source, side, size, offset, volume — sent as you drag,
                   because they are dialled in by eye against the actual scene. */

const fail = (error: unknown) => toast(errorText(error), "error");

interface Props {
  presets: Preset[];
  voices: Voice[];
  models: Model[];
  /* Which browser sources are actually connected, live off the presence frame
     rather than off the preset rows — a source coming and going writes nothing. */
  stages: StageStatus[];
  /** Refetch the list. Every write answers with the row, but the number keys and
      the lock are shared state that another admin may also be editing. */
  onChanged: () => void;
}

export function PresetManager({ presets, voices, models, stages, onChanged }: Props) {
  const [open, setOpen] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);

  return (
    <div className="preset-manager">

      {presets.length === 0 && !adding && (
        <div className="hint">No characters yet.</div>
      )}

      {presets.map((preset, index) => (
        <PresetCard
          key={preset.id}
          preset={preset}
          index={index}
          voices={voices}
          models={models}
          presence={stages.find((stage) => stage.stage === preset.id)}
          expanded={open === preset.id}
          onToggle={() => setOpen(open === preset.id ? null : preset.id)}
          onChanged={onChanged}
        />
      ))}

      {adding ? (
        <PresetForm
          voices={voices}
          models={models}
          onCancel={() => setAdding(false)}
          onSubmit={async (body) => {
            await api("/api/admin/presets", {
              method: "POST",
              body: JSON.stringify(body),
            });
            setAdding(false);
            onChanged();
            toast("Character added", "ok");
          }}
        />
      ) : (
        <button
          className="ghost"
          disabled={presets.length >= 9}
          title={presets.length >= 9 ? "Maximum of 9" : ""}
          onClick={() => setAdding(true)}
        >
          Add a character
        </button>
      )}
    </div>
  );
}

function PresetCard({
  preset,
  index,
  voices,
  models,
  presence,
  expanded,
  onToggle,
  onChanged,
}: {
  preset: Preset;
  index: number;
  voices: Voice[];
  models: Model[];
  presence: StagePresence | undefined;
  expanded: boolean;
  onToggle: () => void;
  onChanged: () => void;
}) {
  const connected = (presence?.connected ?? 0) > 0;

  async function patchOverlay(patch: Partial<Record<string, unknown>>) {
    try {
      await api(`/api/admin/presets/${preset.id}/overlay`, {
        method: "POST",
        body: JSON.stringify(patch),
      });
      onChanged();
    } catch (error) {
      fail(error);
    }
  }

  return (
    <div className={"preset-card" + (expanded ? " open" : "")}>
      <div className="preset-card-head">
        <kbd>{index + 1}</kbd>
        <button className="link preset-card-name" onClick={onToggle}>
          {preset.name}
        </button>
        <span className="muted mono">{preset.voice_name}</span>
        {preset.overlay.own_source && (
          <span
            className={`dot ${connected ? "on" : "off"}`}
            title={
              connected
                ? "Connected"
                : "Not connected"
            }
          />
        )}
        <span className="spacer" />
        {/* The URL without opening the card. It is the one thing on this row
            somebody wants while the stream is up - rebuilding a scene, or
            sending it to whoever runs OBS - and the editor below is not. */}
        {preset.overlay.own_source && preset.overlay_url && (
          <button
            className="ghost"
            title="Copy OBS URL"
            onClick={() =>
              void copyToClipboard(preset.overlay_url ?? "", `${preset.name} URL copied`)
            }
          >
            Copy URL
          </button>
        )}
        {/* The per-preset half of the lock. One click, no form: it gets pressed
            while the stream is running. */}
        <button
          className={preset.locked ? "ghost danger" : "ghost"}
          title={
            preset.locked
              ? "Admins only"
              : "Everyone"
          }
          onClick={async () => {
            try {
              await api(`/api/admin/presets/${preset.id}/lock`, {
                method: "POST",
                body: JSON.stringify({ locked: !preset.locked }),
              });
              onChanged();
            } catch (error) {
              fail(error);
            }
          }}
        >
          {preset.locked ? "Locked" : "Unlocked"}
        </button>
        <button className="ghost" onClick={onToggle}>
          {expanded ? "Done" : "Edit"}
        </button>
      </div>

      {expanded && (
        <div className="preset-card-body">
          <PresetForm
            preset={preset}
            voices={voices}
            models={models}
            onSubmit={async (body) => {
              await api(`/api/admin/presets/${preset.id}`, {
                method: "PUT",
                body: JSON.stringify(body),
              });
              onChanged();
              toast("Character saved", "ok");
            }}
            onDelete={async () => {
              const confirmed = await confirmDialog({
                title: `Delete ${preset.name}?`,
                body: "Its OBS source will stop working. Past messages are kept.",
                confirmLabel: "Delete",
                danger: true,
              });
              if (!confirmed) return;
              try {
                await api(`/api/admin/presets/${preset.id}`, { method: "DELETE" });
                onChanged();
                toast(`${preset.name} deleted`, "ok");
              } catch (error) {
                fail(error);
              }
            }}
          />

          <StageControls
            preset={preset}
            presence={presence}
            onPatch={patchOverlay}
            onChanged={onChanged}
          />
        </div>
      )}
    </div>
  );
}

/* Name, voice, model, tuning. Saved as a document rather than field by field:
   these are what the character *is*, and half of a rename reaching the number
   row mid-stream would be worse than a save button. */
function PresetForm({
  preset,
  voices,
  models,
  onSubmit,
  onCancel,
  onDelete,
}: {
  preset?: Preset;
  voices: Voice[];
  models: Model[];
  onSubmit: (body: Record<string, unknown>) => Promise<void>;
  onCancel?: () => void;
  onDelete?: () => void;
}) {
  const [name, setName] = useState(preset?.name ?? "");
  const [voiceId, setVoiceId] = useState(preset?.voice_id ?? voices[0]?.voice_id ?? "");
  const [modelId, setModelId] = useState(
    preset?.model_id ?? models[0]?.model_id ?? "eleven_multilingual_v2",
  );
  const [settings, setSettings] = useState<VoiceSettings>({
    ...DEFAULT_SETTINGS,
    ...(preset?.settings ?? {}),
  });
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await onSubmit({
        name,
        voice_id: voiceId,
        model_id: modelId,
        settings,
        locked: preset?.locked ?? false,
        // Carried through untouched: this form is not where staging is edited,
        // and omitting it would reset the character to the middle of the screen.
        overlay: preset?.overlay,
      });
    } catch (error) {
      fail(error);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="preset-form">
      <label>
        Name
        <input
          type="text"
          maxLength={24}
          value={name}
          placeholder="Grandma"
          onChange={(event) => setName(event.target.value)}
        />
      </label>
      <label>
        Voice
        <select value={voiceId} onChange={(event) => setVoiceId(event.target.value)}>
          {/* A voice the key can no longer see still has to show as itself,
              rather than silently becoming whatever is first in the list. */}
          {voiceId && !voices.some((voice) => voice.voice_id === voiceId) && (
            <option value={voiceId}>
              {preset?.voice_name ?? voiceId} (not on this account)
            </option>
          )}
          {voices.map((voice) => (
            <option key={voice.voice_id} value={voice.voice_id}>
              {voice.name}
            </option>
          ))}
        </select>
      </label>
      {/* No label here: ModelPicker prints its own, and the two stacked up as
          "MODEL" twice over the same select. */}
      <ModelPicker
        models={models}
        modelId={modelId}
        onChange={(next) => {
          setModelId(next);
          if (isV3(next)) {
            setSettings((current) => ({
              ...current,
              stability: snapStability(current.stability),
            }));
          }
        }}
      />
      <VoiceSettingsForm modelId={modelId} settings={settings} onChange={setSettings} />

      <div className="field-row" style={{ marginTop: 10 }}>
        <button disabled={saving || !name.trim() || !voiceId} onClick={() => void save()}>
          {saving ? "Saving…" : preset ? "Save character" : "Add character"}
        </button>
        {onCancel && (
          <button className="ghost" onClick={onCancel}>
            Cancel
          </button>
        )}
        {onDelete && (
          <button className="ghost danger" onClick={onDelete}>
            Delete
          </button>
        )}
      </div>
    </div>
  );
}

/* Where the character stands, how loud it is, and whether it is its own browser
   source. Sent as you drag, throttled — you size a character against the actual
   scene, not by typing a number and hoping. */
function StageControls({
  preset,
  presence,
  onPatch,
  onChanged,
}: {
  preset: Preset;
  presence: StagePresence | undefined;
  onPatch: (patch: Record<string, unknown>) => Promise<void>;
  onChanged: () => void;
}) {
  const overlay = preset.overlay;
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(timer.current), []);

  const push = (patch: Record<string, unknown>) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => void onPatch(patch), 120);
  };

  return (
    <div className="stage-controls">
      <label className="check">
        <input
          type="checkbox"
          checked={overlay.own_source}
          onChange={(event) => void onPatch({ own_source: event.target.checked })}
        />
        Separate OBS source
      </label>

      {overlay.own_source && (
        <>
          <SourceHealth presence={presence} />
          <div className="field-row">
            <input
              type="text"
              readOnly
              value={preset.overlay_url ?? ""}
              onClick={(event) => event.currentTarget.select()}
            />
            <button
              className="ghost"
              onClick={() =>
                void copyToClipboard(preset.overlay_url ?? "", `${preset.name} URL copied`)
              }
            >
              Copy
            </button>
          </div>

        </>
      )}

      <PresetSlider
        label="Volume"
        value={overlay.volume}
        min={0}
        max={1}
        step={0.05}
        format={(value) => `${Math.round(value * 100)}%`}
        onChange={(volume) => push({ volume })}
      />

      {preset.tuber && (
        <div style={{ marginTop: 14 }}>
          <div className="field-label" style={{ fontWeight: 600 }}>
            PNGtuber
          </div>
          <TuberControls
            tuber={preset.tuber}
            presetId={preset.id}
            presetName={preset.name}
            onState={onChanged}
          />
        </div>
      )}
    </div>
  );
}

function SourceHealth({ presence }: { presence: StagePresence | undefined }) {
  useTick(); // "12s ago" counts on its own
  const connected = (presence?.connected ?? 0) > 0;
  const seenAt = presence?.seen_at ?? null;
  return (
    <div className={"health" + (connected ? " ok" : "")} style={{ marginTop: 10 }}>
      <div className="health-line">
        <span className={`dot ${connected ? "on" : "off"}`} />
        <b>{connected ? "Connected" : "Not connected"}</b>
      </div>
      <div className="hint">
        {connected
          ? `Last seen ${seenAt ? timeAgo(seenAt) : "just now"}`
          : "Add the URL below as a browser source in OBS."}
      </div>
    </div>
  );
}

function PresetSlider({
  label,
  value,
  min,
  max,
  step,
  format,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  format: (value: number) => string;
  onChange: (value: number) => void;
}) {
  const [focused, focus] = useIsFocused();
  const [local, setLocal] = useState(value);

  // Another admin's change follows through — unless this panel is dragging.
  useEffect(() => {
    if (!focused) setLocal(value);
  }, [value, focused]);

  return (
    <div className="slider" style={{ marginTop: 12, marginBottom: 0 }}>
      <label>
        <span>{label}</span>
        <span className="slider-val">{format(local)}</span>
      </label>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={local}
        {...focus}
        onChange={(event) => {
          const next = parseFloat(event.target.value);
          setLocal(next);
          onChange(next);
        }}
      />
    </div>
  );
}
